import { Alert, Checkbox, Input, Space, Table } from 'antd';
import TextArea from 'antd/lib/input/TextArea';
import { ColumnType } from 'antd/lib/table';
import { RequiredColumnTitle } from 'components/common/RequiredColumnTitle';
import { useFormikContext } from 'formik';
import { DefectiveReturnItemPayload, SparingRequestLineItemPayload, SparingRequestPayload } from 'models/SparingRequests';
import { useEffect, useState } from 'react';
import { useGetSparingRequestConfigurationTypesQuery } from 'redux/services/calvin/sparingRequestConfigTypes';
import { alignDefectiveReturnItemsToUnits, buildDefaultDefectiveReturnItem, expandLineItemsToDefectiveReturnUnits, getDefectiveReturnsRequiredInfoErrors } from 'utils/defectiveReturns';

type DefectiveReturnRow = SparingRequestLineItemPayload & {
  serialNumber: string;
  caseNumber: string;
  rmaNumber: string;
  rowKey: string;
  rowIndex: number;
};

export const DefectiveReturnsTable = (): JSX.Element => {
  const { values, setFieldValue } = useFormikContext<SparingRequestPayload>();
  const { data: requestTypesData, isLoading: isRequestTypesLoading } = useGetSparingRequestConfigurationTypesQuery();

  // MDSI1-4304: the shared helper is the only source of the required-information verdict, so the
  // cells, the Post button, both layouts' validate and the step icon cannot disagree.
  const selectedType = requestTypesData?.data?.find((t) => t.id === values.sparingRequestTypeId);
  const requiredInfo = getDefectiveReturnsRequiredInfoErrors({
    items: values.defectiveReturns?.items,
    lineItems: values.lineItems,
    requestType: selectedType
  });

  // One row per unit, in the order that defectiveReturns.items has to follow.
  const units = expandLineItemsToDefectiveReturnUnits(values.lineItems);

  const singleProductsArray: DefectiveReturnRow[] = units.map((unit, rowIndex) => ({
    ...unit.line,
    quantity: 1,
    serialNumber: '',
    caseNumber: '',
    rmaNumber: '',
    rowKey: unit.rowKey,
    rowIndex
  }));

  const isRequirementActive = requiredInfo.isRequirementActive;
  const currentItems = values.defectiveReturns?.items;
  // Content identity of the rendered rows: product, position and quantity. Adding more of a product
  // already in the cart moves its line to the end at an identical lineItems.length.
  const rowsSignature = singleProductsArray.map((row) => row.rowKey).join('|');

  // Keep defectiveReturns.items in step with the rendered rows.
  //
  // Requirement OFF: main's behaviour, unchanged - on mount and whenever lineItems.length changes,
  // if the item count no longer matches the row count, rebuild every row as a blank default.
  //
  // Requirement ON (MDSI1-4304): match the stored items to the rows by product identity, so what the
  // user typed survives this step unmounting, the cart being re-ordered and quantities changing, and
  // re-sync whenever the items array itself is replaced (a formik reinitialise). Nothing is written
  // unless the matched result differs, so this cannot loop.
  //
  // Both branches wait for the request types, because the mode is not known until they arrive and
  // running main's blank rebuild first would wipe the stored items the requirement-on branch has to
  // align. While they load the table renders no rows (see the return below), so nothing can be typed
  // into an item that the rebuild then replaces - and once they arrive the right branch runs on the
  // items exactly as they were at mount, which with the requirement off is main's mount-time rebuild.
  useEffect(() => {
    if (isRequestTypesLoading) return;

    const items = currentItems ?? [];

    if (!isRequirementActive) {
      if (items.length !== units.length) {
        setFieldValue('defectiveReturns', {
          contactName: values.contactName ?? '',
          contactPhoneNumber: values.contactPhoneNumber ?? '',
          contactEmail: values.contactEmail ?? '',
          waybillDeliveryMethods: values.defectiveReturns?.waybillDeliveryMethods,
          items: units.map(buildDefaultDefectiveReturnItem)
        });
      }

      return;
    }

    const alignedItems = alignDefectiveReturnItemsToUnits(items, values.lineItems);
    const isUnchanged = alignedItems.length === items.length && alignedItems.every((item, index) => item === items[index]);

    if (isUnchanged) return;

    setFieldValue('defectiveReturns', {
      ...values.defectiveReturns,
      contactName: values.defectiveReturns?.contactName ?? values.contactName ?? '',
      contactPhoneNumber: values.defectiveReturns?.contactPhoneNumber ?? values.contactPhoneNumber ?? '',
      contactEmail: values.defectiveReturns?.contactEmail ?? values.contactEmail ?? '',
      waybillDeliveryMethods: values.defectiveReturns?.waybillDeliveryMethods,
      items: alignedItems
    });
    // The requirement-off inputs are exactly main's (lineItems.length); the row signature and the
    // items array only take part while the requirement is on.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRequestTypesLoading, isRequirementActive, values.lineItems.length, isRequirementActive ? rowsSignature : '', isRequirementActive ? currentItems : undefined]);

  // Rows are addressed by their own rowIndex rather than by the index antd hands to a cell
  // renderer, which is relative to the rendered page rather than to the whole data source.
  const updateItem = (rowIndex: number, changes: Partial<DefectiveReturnItemPayload>): void => {
    const items = (values.defectiveReturns?.items ?? []).slice();

    if (!items[rowIndex]) return;

    items[rowIndex] = { ...items[rowIndex], ...changes };

    setFieldValue('defectiveReturns.items', items);
  };

  // The rest of the request form only turns a required field red once it has been touched (see
  // FieldInput: it clears touched on focus and formik sets it again on blur), so a field the user
  // has not reached yet shows nothing but the asterisk on its label. A table has no per-row label,
  // so each cell tracks its own touched state to read the same way.
  const [touchedCells, setTouchedCells] = useState<Record<string, boolean>>({});
  const cellKey = (rowIndex: number, field: string): string => `${rowIndex}:${field}`;
  const setCellTouched = (rowIndex: number, field: string, isTouched: boolean): void => setTouchedCells((previous) => ({ ...previous, [cellKey(rowIndex, field)]: isTouched }));

  /**
   * Renders a required defective-return cell the way the rest of the request form renders one: a
   * #dc3545 border and message only once the cell has been touched, with the message always in the
   * DOM so showing it never shifts the row.
   */
  const renderRequiredCell = (rowIndex: number, field: 'productNumber' | 'serialNumber', value: string): JSX.Element => {
    const error = requiredInfo.rowErrors[rowIndex]?.[field];
    const showError = Boolean(error) && touchedCells[cellKey(rowIndex, field)];

    return (
      <>
        <Input
          value={value}
          style={{ borderColor: showError ? '#dc3545' : undefined }}
          onFocus={(): void => setCellTouched(rowIndex, field, false)}
          onBlur={(): void => setCellTouched(rowIndex, field, true)}
          onChange={(e): void => updateItem(rowIndex, { [field]: e.target.value })}
        />
        <div className={showError ? 'field-error-show' : 'field-error-hide'}>{error}</div>
      </>
    );
  };

  const cols: ColumnType<DefectiveReturnRow>[] = [
    {
      title: `Didn't Use`,
      render: (_value, record): JSX.Element => {
        const isUsed = values.defectiveReturns?.items?.[record.rowIndex]?.used ?? true;

        return <Checkbox checked={!isUsed} onChange={(e): void => updateItem(record.rowIndex, { used: !e.target.checked })} />;
      },
      align: 'center',
      width: '12ch'
    },
    {
      title: requiredInfo.isRequirementActive ? <RequiredColumnTitle text="Return Part Number" /> : 'Return Part Number',
      dataIndex: 'productNumber',
      ellipsis: true,
      width: '25ch',
      render: (_value, record): JSX.Element => renderRequiredCell(record.rowIndex, 'productNumber', values.defectiveReturns?.items?.[record.rowIndex]?.productNumber ?? record.productNumber)
    },
    {
      title: requiredInfo.isRequirementActive ? <RequiredColumnTitle text="Return Serial Number" /> : 'Return Serial Number',
      dataIndex: 'serialNumber',
      render: (_value, record): JSX.Element => renderRequiredCell(record.rowIndex, 'serialNumber', values.defectiveReturns?.items?.[record.rowIndex]?.serialNumber ?? '')
    },
    {
      title: 'Qty',
      dataIndex: 'quantity',
      align: 'center',
      width: '9ch'
    },
    {
      title: 'OEM Case Number',
      dataIndex: 'caseNumber',
      render: (_value, record): JSX.Element => {
        const effectiveCase = values.defectiveReturns?.items?.[record.rowIndex]?.caseNumber ?? '';

        return <Input value={effectiveCase} onChange={(e): void => updateItem(record.rowIndex, { caseNumber: e.target.value })} />;
      }
    },
    {
      title: 'OEM RMA Number',
      dataIndex: 'rmaNumber',
      render: (_value, record): JSX.Element => {
        const effectiveRma = values.defectiveReturns?.items?.[record.rowIndex]?.rmaNumber ?? '';

        return <Input value={effectiveRma} onChange={(e): void => updateItem(record.rowIndex, { rmaNumber: e.target.value })} />;
      }
    },
    {
      title: 'Notes',
      dataIndex: 'notes',
      render: (_value, record): JSX.Element => {
        const effectiveNotes = values.defectiveReturns?.items?.[record.rowIndex]?.notes ?? '';

        return <TextArea rows={2} value={effectiveNotes} onChange={(e): void => updateItem(record.rowIndex, { notes: e.target.value })} />;
      }
    }
  ];

  // While the request types load there are no rows and so no inputs - see the sync above.
  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      {requiredInfo.isRequirementActive && !isRequestTypesLoading && (
        <Alert type="info" showIcon message="Return Part Number and Return Serial Number are required for every item being returned before this request can be posted." />
      )}
      <Table className="narrow-table" rowKey={(record): string => record.rowKey} loading={isRequestTypesLoading} dataSource={isRequestTypesLoading ? [] : singleProductsArray} columns={cols} />
    </Space>
  );
};
